import React, {useEffect, useRef, useState} from "react"
import trackShare from "../../library/sharing/ShareTracker"
import ReactGA from "react-ga"
import {ShareSheetProps} from "./ShareSheet.types"
import {ShareOption} from "../../library/sharing/ShareOption"
import CopyLinkButton from "./ShareButton/CopyLinkButton"
import XButton from "./ShareButton/XButton"
import FacebookButton from "./ShareButton/FacebookButton"
import DownloadButton from "./ShareButton/DownloadButton"
import EmailForm from "./EmailForm/EmailForm"
import ManualSaveView from "./ManualSaveView/ManualSaveView"
import stylesheet from "./ShareSheet.module.scss"
import CloseIcon from "../../resource/icon/close.svg"
import NewWindowIcon from "../../resource/icon/new-window.svg"
import LinkedInButton from "./ShareButton/LinkedInButton"

const ShareSheet: React.FC<ShareSheetProps> = ({
  heading,
  customSharePrompt,
  facebookAppId,
  prefillTweetText,
  hashtags,
  shareOptions,
  upload,
  showNewWindowButton,
  onHide
}) => {
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailIsForInstagram, setEmailIsForInstagram] = useState(false)
  const [showTapAndHoldToSave, setShowTapAndHoldToSave] = useState(false)

  const textAreaRef = useRef(null)

  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px"
    }
  })

  const ALL_SHARE_OPTIONS: ShareOption[] = [
    ShareOption.INSTAGRAM,
    ShareOption.TWITTER,
    ShareOption.DOWNLOAD,
    ShareOption.COPY_LINK,
    ShareOption.FACEBOOK,
    ShareOption.LINKEDIN,
  ]

  const track = (trackUrl: string, gaLabel: string) => {
    try {
      ReactGA.event({
        category: 'Share Sheet',
        action: `Share Button Click - ${gaLabel}`,
        label: `Share ${upload.layout} to ${gaLabel}`
      })
    } catch {
      console.error("Google Analytics tracking failed")
    }
    if (trackUrl) trackShare(trackUrl)
  }

  if (showTapAndHoldToSave) {
    return (
      <ManualSaveView
        imageSrc={upload.media.original}
        aspectRatio={upload.dimensions.aspect}
        layout={upload.layout}
        onClose={() => setShowTapAndHoldToSave(false)}
      />
    )
  }

  if (showEmailForm) {
    return (
      <EmailForm
        uploadCode={upload.code}
        formatMediaForInstagram={emailIsForInstagram}
        layout={upload.layout}
        onClose={() => setShowEmailForm(false)}
      />
    )
  }

  return (
    <div className={stylesheet.shareSheet}>
      <button className={stylesheet.closeButton} onClick={onHide} style={{ backgroundImage: `url(${CloseIcon})` }}></button>

      <h2 className={stylesheet.heading}>{heading}
        {showNewWindowButton &&
          <a className={stylesheet.newWindowButton} href={upload.links.self} target="_blank" rel="noreferrer">
            <img src={NewWindowIcon} />
          </a>
        }
      </h2>
      <div className={stylesheet.shareButtonContainer}>
        {(shareOptions ?? ALL_SHARE_OPTIONS).map((option, index) => {
          let commonProps = {
            "data-testid": option,
            key: option,
            order: index
          }

          switch (option) {
            case ShareOption.INSTAGRAM:
              return <DownloadButton
                instagram
                {...commonProps}
                upload={upload}
                trackClick={() => track(upload.links.trackInstagram, "Instagram")}
                handleEmail={() => {
                  setEmailIsForInstagram(true)
                  setShowEmailForm(true)
                }}
                handleTapAndHoldToSave={() => setShowTapAndHoldToSave(true)}
              />
            case ShareOption.FACEBOOK:
              return <FacebookButton
                {...commonProps}
                shareUrl={upload.links.self}
                hashtags={hashtags}
                facebookAppId={facebookAppId}
                trackClick={() => track(upload.links.trackFacebook, "Facebook")}
              />
            case ShareOption.TWITTER:
              return <XButton
                {...commonProps}
                shareUrl={upload.links.self}
                hashtags={hashtags}
                prefillTweetText={prefillTweetText}
                trackClick={() => track(upload.links.trackTwitter, "Twitter")}
              />
            case ShareOption.LINKEDIN:
              return <LinkedInButton
                {...commonProps}
                shareUrl={upload.links.self}
                trackClick={() => track(upload.links.trackLinkedIn, "LinkedIn")}
              />
            case ShareOption.COPY_LINK:
              return <CopyLinkButton
                {...commonProps}
                shareUrl={upload.links.self}
                trackClick={() => track(upload.links.trackCopyLink, "Copy Link")}
              />
            default:
              return <DownloadButton
                {...commonProps}
                upload={upload}
                trackClick={() => track(upload.links.trackDownload, "Download")}
                handleEmail={() => {
                  setEmailIsForInstagram(false)
                  setShowEmailForm(true)
                }}
                handleTapAndHoldToSave={() => setShowTapAndHoldToSave(true)}
              />
          }
        })
        }
      </div>

      {customSharePrompt &&
        <div className={stylesheet.sharePrompt} data-testid="sharePrompt">
          {customSharePrompt}
        </div>
      }

      {hashtags && !customSharePrompt && hashtags !== "" &&
        <textarea
          ref={textAreaRef}
          className={stylesheet.hashtags}
          value={hashtags}
          readOnly
          rows={1}
          data-testid="hashtags"
          onClick={(e) => {
            (e.target as HTMLTextAreaElement).select()
          }}
        />
      }
    </div >
  )
}

export default ShareSheet